import React, { useState, useEffect, ReactElement, useMemo } from 'react';
import MasterConfig from '../../config/Master';
import Checkbox from '../../atoms/Checkbox';
import styled, { CSSObject } from '@emotion/styled';
import { getBackgroundTheme, getPatternModal, getTextColor } from '../../style';
import { isDarkMode, IsMobileViewPort } from '../../utils/helpers';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import { updateMsgReminder } from '../../actions/profileActions';
import { useMediaQuery, useTheme } from '@material-ui/core';
import ModalWithPattern from 'components/common/modals/ModalWithPattern';
import Button from '../../atoms/buttons/Button';
import ButtonSet from 'components/blocks/ButtonSet';

const ReminderLinks = styled('div')(
  (): CSSObject => ({
    fontSize: 13,
    fontWeight: 400,
    lineHeight: '17px',
    textAlign: 'center',
    textDecoration: !isDarkMode() ? 'underline #1383d2' : 'underline #AFB0B1',
    textUnderlinePosition: 'under',
    a: {
      ...getTextColor().LINK,
    },
    marginTop: 30,
    marginBottom: 10,
  })
);

const SubHadingContent = styled('p')({
  fontSize: 14,
  fontWeight: 400,
  lineHeight: '19px',
  textAlign: 'center',
  margin: 0,
});

const ContentList = styled('li')({
  fontSize: 14,
  lineHeight: '19px',
  fontWeight: 600,
  padding: '10px 0',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
});

const mobileOverlayStyle = {
  borderRadius: 10,
  height: '100%',
  width: '90%',
  padding: '0 10%',
  left: '-5%',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
};

const mobileContentStyle = (): CSSObject => ({
  borderRadius: 10,
  top: '50%',
  height: 'auto',
  transform: 'translate(0, -50%)',
  background: isDarkMode() ? '#212121' : '#ffffff',
  border: 'unset',
});

const TitleContainer = styled('h3')(() => ({
  fontStyle: 'normal',
  fontWeight: 700,
  color: getPatternModal().titleColor,
  fontSize: 28,
  lineHeight: '24px',
  letterSpacing: '-0.3px',
  marginTop: 20,
  marginBottom: 20,
}));

const customLabelTextStyle = {
  fontSize: 14,
  fontWeight: 600,
  lineHeight: '19px',
  display: 'block',
  marginLeft: 30,
  paddingLeft: 0,
};

const checkBoxCustomStyle = (): React.CSSProperties => ({
  display: 'block',
  float: 'left' as React.CSSProperties['float'],
  border: isDarkMode() ? '1px solid #dbdbdb' : '1px solid #575757',
});

const ReminderModal = (): ReactElement => {
  const dispatch = useDispatch();
  const authUser = useSelector((state: RootState) => state.profile);
  const acctType = useMemo(
    () => (authUser && authUser.profile && authUser.profile.data.account_type) ?? null,
    [authUser]
  );
  const reminderStatus = useMemo(() => authUser.account_config?.show_message_reminder, [authUser.account_config]);

  const [modalOpen, setModalOpen] = useState(false);
  const [reminderOption1, setReminderOption1] = useState(false);
  const [reminderOption2, setReminderOption2] = useState(false);
  const [reminderOption3, setReminderOption3] = useState(false);
  const [reminderOption4, setReminderOption4] = useState(false);
  const [reminderOption5, setReminderOption5] = useState(false);
  const [allChecked, setAllChecked] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(1024.05));
  const isTablet = useMediaQuery(theme.breakpoints.between(712, 835));

  useEffect(() => {
    if (reminderStatus !== 0) {
      setModalOpen(true);
    }
  }, [reminderStatus]);

  useEffect(() => {
    reminderStatus === 2
      ? setAllChecked(reminderOption1 && reminderOption2 && reminderOption3)
      : setAllChecked(reminderOption1 && reminderOption2 && reminderOption3 && reminderOption4 && reminderOption5);
  }, [reminderOption1, reminderOption2, reminderOption3, reminderOption4, reminderOption5, reminderStatus]);

  const closeModal = () => {
    setModalOpen(false);
    dispatch(updateMsgReminder());
  };

  const updateReminderOptions = (index: number, status: boolean | ((prevState: boolean) => boolean)) => {
    switch (index) {
      case 1:
        setReminderOption1(status);
        break;
      case 2:
        setReminderOption2(status);
        break;
      case 3:
        setReminderOption3(status);
        break;
      case 4:
        setReminderOption4(status);
        break;
      case 5:
        setReminderOption5(status);
        break;
    }
  };

  const displayContent = () => {
    switch (acctType) {
      case 'Generous':
        return (
          <div>
            <SubHadingContent>As a member you agree to:</SubHadingContent>
            <ul style={{ listStyleType: 'none', margin: '30px 20px', padding: 0 }}>
              <ContentList style={{ paddingTop: 0 }}>
                <Checkbox
                  inline
                  noSpace
                  label="Be respectful and abide by the Community Guidelines at all times."
                  value={reminderOption1}
                  id="reminder_opt1"
                  onChange={() => updateReminderOptions(1, !reminderOption1)}
                  customStyle={checkBoxCustomStyle()}
                  customLabelTextStyle={customLabelTextStyle}
                />
              </ContentList>
              <ContentList>
                <Checkbox
                  inline
                  noSpace
                  label="Report any inappropriate or suspicious behavior."
                  value={reminderOption2}
                  id="reminder_opt2"
                  onChange={() => updateReminderOptions(2, !reminderOption2)}
                  customStyle={checkBoxCustomStyle()}
                  customLabelTextStyle={customLabelTextStyle}
                />
              </ContentList>
              <ContentList>
                <Checkbox
                  inline
                  noSpace
                  label="Read the Safe Dating Tips and be cautious while interacting with other members."
                  value={reminderOption3}
                  id="reminder_opt3"
                  onChange={() => updateReminderOptions(3, !reminderOption3)}
                  customStyle={checkBoxCustomStyle()}
                  customLabelTextStyle={customLabelTextStyle}
                />
              </ContentList>
              {reminderStatus !== 2 && (
                <div>
                  <ContentList>
                    <Checkbox
                      inline
                      noSpace
                      label="Give the First Date Incentive on your date as reimbursement for your date's expenses."
                      value={reminderOption4}
                      id="reminder_opt4"
                      onChange={() => updateReminderOptions(4, !reminderOption4)}
                      customStyle={checkBoxCustomStyle()}
                      customLabelTextStyle={customLabelTextStyle}
                    />
                  </ContentList>
                  <ContentList>
                    <Checkbox
                      inline
                      noSpace
                      label="Not renegotiate the agreed amount or ask for the First Date Incentive in advance."
                      value={reminderOption5}
                      id="reminder_opt5"
                      onChange={() => updateReminderOptions(5, !reminderOption5)}
                      customStyle={checkBoxCustomStyle()}
                      customLabelTextStyle={customLabelTextStyle}
                    />
                  </ContentList>
                </div>
              )}
            </ul>
          </div>
        );
      case 'Attractive':
        return (
          <div>
            <SubHadingContent>As a member you agree to:</SubHadingContent>
            <ul style={{ listStyleType: 'none', margin: '30px 20px', padding: 0 }}>
              <ContentList style={{ paddingTop: 0 }}>
                <Checkbox
                  inline
                  noSpace
                  label="Be respectful and abide by the Community Guidelines at all times."
                  value={reminderOption1}
                  id="reminder_opt1"
                  onChange={() => updateReminderOptions(1, !reminderOption1)}
                  customStyle={checkBoxCustomStyle()}
                  customLabelTextStyle={customLabelTextStyle}
                />
              </ContentList>
              <ContentList>
                <Checkbox
                  inline
                  noSpace
                  label="Report any inappropriate or suspicious behavior."
                  value={reminderOption2}
                  id="reminder_opt2"
                  onChange={() => updateReminderOptions(2, !reminderOption2)}
                  customStyle={checkBoxCustomStyle()}
                  customLabelTextStyle={customLabelTextStyle}
                />
              </ContentList>
              <ContentList>
                <Checkbox
                  inline
                  noSpace
                  label="Read the Safe Dating Tips and be cautious while interacting with other members."
                  value={reminderOption3}
                  id="reminder_opt3"
                  onChange={() => updateReminderOptions(3, !reminderOption3)}
                  customStyle={checkBoxCustomStyle()}
                  customLabelTextStyle={customLabelTextStyle}
                />
              </ContentList>
              {reminderStatus !== 2 && (
                <div>
                  <ContentList>
                    <Checkbox
                      inline
                      noSpace
                      label="Accept the First Date Incentive on your date as reimbursement for your expenses."
                      value={reminderOption4}
                      id="reminder_opt4"
                      onChange={() => updateReminderOptions(4, !reminderOption4)}
                      customStyle={checkBoxCustomStyle()}
                      customLabelTextStyle={customLabelTextStyle}
                    />
                  </ContentList>
                  <ContentList>
                    <Checkbox
                      inline
                      noSpace
                      label="Not renegotiate the agreed amount or ask for the First Date Incentive in advance."
                      value={reminderOption5}
                      id="reminder_opt5"
                      onChange={() => updateReminderOptions(5, !reminderOption5)}
                      customStyle={checkBoxCustomStyle()}
                      customLabelTextStyle={customLabelTextStyle}
                    />
                  </ContentList>
                </div>
              )}
            </ul>
          </div>
        );
      default:
        return <div />;
    }
  };

  const renderLinks = () => {
    return (
      <ReminderLinks>
        <a
          data-test-id={'cypress-reminder-link-safe-dating'}
          href={`${MasterConfig.FULL_URL}/blog/tips-for-dating-safely-during-covid-and-after`}
          target="_blank"
          rel="noreferrer"
        >
          Read Safe Dating Tips
        </a>
        <div style={{ paddingTop: 10 }} />
        <a
          data-test-id={'cypress-reminder-link-community-guidelines'}
          href={`${MasterConfig.FULL_URL}/community-guidelines`}
          target="_blank"
          rel="noreferrer"
        >
          Read Community Guidelines
        </a>
      </ReminderLinks>
    );
  };

  return (
    <div>
      <ModalWithPattern
        dataTestID="message-reminder-modal"
        isModalOpen={modalOpen}
        modalContentLabel="Dating Safely"
        closeOnOverlayClick={false}
        closeRequest={closeModal}
        closeBtnClassName="message-reminder-modal-close"
        closeBtnDataTestId="message-reminder-modal-close"
        onCloseClick={closeModal}
        customOverlayStyle={IsMobileViewPort ? mobileOverlayStyle : {}}
        customContentStyle={
          IsMobileViewPort
            ? mobileContentStyle()
            : {
                maxWidth: isMobile && isTablet ? 520 : 620,
                background: getBackgroundTheme().block2,
                border: 'unset',
              }
        }
        hideCloseBtn
      >
        <TitleContainer>{`Dating Safely`}</TitleContainer>
        {displayContent()}

        <ButtonSet align={'center'}>
          <Button
            buttonType="primaryRed"
            onClick={closeModal}
            disabled={!allChecked}
            customStyle={{ padding: '12px 22px' }}
          >
            I agree
          </Button>
        </ButtonSet>
        {renderLinks()}
      </ModalWithPattern>
    </div>
  );
};

export default ReminderModal;
